import React, { useState, useEffect, useCallback } from 'react'
import { NumericFormat } from 'react-number-format'
import {
  Typography, Button, IconButton, Chip, CircularProgress,
  Table, TableBody, TableRow, TableHead, TableContainer, TableCell,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Pagination,
  Grid, TextField, FormGroup, FormControlLabel, Checkbox,
  FormControl, InputLabel, Select, MenuItem, ListItemText, OutlinedInput, Box,
  Paper, Stack, Tooltip
} from '@mui/material'
import { Add, Edit, DeleteForever, Search as SearchIcon, FileDownload } from '@mui/icons-material'
import { Colors } from '../styles/theme'
import * as Yup from 'yup'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { useAppContext } from '../context/AppProvider'
import Dropzone from './ImageUpload/Dropzone'
import { useS3 } from '../hooks/useS3'
import DeleteConfirmationDialog from './DeleteConfirmationDialog'
import { useItems } from '../hooks/useItems'
import { useSearchParams } from 'react-router-dom'

const validationData = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  price: Yup.number().required('Name is required').positive('Must be a positive number'),
  discount: Yup.mixed(),
  brand: Yup.string().required('Brand is required'),
  categories: Yup.array().required('Categories is required'),
  bestprice: Yup.bool(),
  clearence: Yup.bool(),
  package: Yup.bool()
})

const keyOptions = [
  { name: 'Cod produs / denumire', value: 'Name' },
  { name: 'Brand', value: 'Brand' },
  { name: 'Categorie', value: 'Category' },
  { name: 'Visibil', value: 'Visibility' },
  { name: 'Pachet', value: 'Package' },
  { name: 'BestPrice', value: 'BestPrice' },
  { name: 'Discount activ', value: 'HasDiscount' },
  { name: 'Outlet', value: 'Clearence' },
  { name: 'Stock', value: 'Stock' },
  { name: 'Preț', value: 'Price' }
]
const operatorOptions = {
  Name: [{ name: 'Conține', value: '=' }, { name: 'Nu conține', value: '!=' }],
  Brand: [{ name: 'Egal', value: '=' }, { name: 'Nui egal', value: '!=' }],
  Category: [{ name: 'Egal', value: '=' }, { name: 'Nui egal', value: '!=' }],
  Visibility: [{ name: 'Egal', value: '=' }],
  Package: [{ name: 'Egal', value: '=' }],
  BestPrice: [{ name: 'Egal', value: '=' }],
  HasDiscount: [{ name: 'Egal', value: '=' }],
  Clearence: [{ name: 'Egal', value: '=' }],
  Stock: [
    { name: 'Mai mare', value: '>' },
    { name: 'Mai mic', value: '<' },
    { name: 'Mai mare sau egal', value: '>=' },
    { name: 'Mai mic sau egal', value: '<=' },
    { name: 'Egal', value: '=' },
    { name: 'Nui egal', value: '!=' }],
  Price: [
    { name: 'Mai mare', value: '>' },
    { name: 'Mai mic', value: '<' },
    { name: 'Mai mare sau egal', value: '>=' },
    { name: 'Mai mic sau egal', value: '<=' },
    { name: 'Egal', value: '=' },
    { name: 'Nui egal', value: '!=' }]
}

const filterKeyLabels = {
  Name: 'Cod produs / denumire',
  Brand: 'Brand',
  Category: 'Categorie',
  Visibility: 'Vizibil',
  Package: 'Pachet',
  BestPrice: 'BestPrice',
  HasDiscount: 'Discount activ',
  Clearence: 'Outlet',
  Stock: 'Stock',
  Price: 'Preț'
}

const boolFilterValues = new Set(['Visibility', 'Package', 'BestPrice', 'HasDiscount', 'Clearence'])

const Products = () => {
  const { state } = useAppContext()
  const [fileForUpload, setFileForUpload] = useState(null)
  const { uploadToS3, downloadFromS3 } = useS3()
  const [selectedKey, setSelectedKey] = useState('')
  const [selectedOperator, setSelectedOperator] = useState('')
  const [selectedValue, setSelectedValue] = useState('')
  const [filters, setFilters] = useState([])
  const [exportLoading, setExportLoading] = useState(false)

  const { addItems, updateItems, deleteItems } = useItems()

  const [open, setOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [selectedBulkDiscountId, setSelectedBulkDiscountId] = useState('')
  const [bulkStatus, setBulkStatus] = useState({ type: '', message: '' })

  const ITEM_HEIGHT = 48
  const ITEM_PADDING_TOP = 8
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250
      }
    }
  }
  const products = state.items.data
  const categories = state.collections.categories.data
  const brands = state.collections.brands.data
  const discounts = state.collections.discount.data
  const [discountedPrice, setDiscountedPrice] = useState(0)
  const [initialValues, setInitialValues] = useState({
    _id: -1,
    name: '',
    description: '',
    price: 0,
    stock: 99,
    images: [],
    cover: '',
    bestprice: false,
    clearence: false,
    package: false,
    discount: -1,
    brand: brands[0],
    ean: '',
    visible: false,
    categories: [categories[0]]
  })

  const handleAddProduct = () => {
    setInitialValues({
      _id: -1,
      name: '',
      description: '',
      price: 0,
      stock: 99,
      cover: '',
      images: [],
      bestprice: false,
      clearence: false,
      package: false,
      discount: -1,
      brand: brands[0]._id,
      ean: '',
      visible: false,
      categories: [categories[0]._id]
    })
    setOpen(true)
  }

  useEffect(() => {
    if (brands[0] !== undefined) {
      setInitialValues(prev => { return { ...prev, brand: brands[0]._id } })
    }
  }, [brands])

  const { fetchItemsByEntityType, exportItems } = useItems()

  const [page, setPage] = useState(1)
  const maxResult = 100
  const totalPages = state.items.total ? Math.ceil(state.items.total / maxResult) : 0
  const [searchParams, setSearchParams] = useSearchParams()
  const discountCollectionIds = new Set(discounts.map(discount => discount._id))

  const paginationHandleChange = (event, value) => {
    if (value !== page) {
      const updatedFilters = { ...Object.fromEntries(searchParams.entries()), page: parseInt(value, 10) }
      setSearchParams(updatedFilters)
      setPage(parseInt(value, 10))
      setSelectedItemIds([])
    }
  }

  const handleDelete = (product) => {
    setBulkStatus({ type: '', message: '' })
    deleteItems(product)
  }

  const handleToggleSelected = (itemId) => {
    setBulkStatus({ type: '', message: '' })
    setSelectedItemIds(prev => prev.includes(itemId)
      ? prev.filter(id => id !== itemId)
      : [...prev, itemId]
    )
  }

  const handleToggleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedItemIds(products.map(product => product._id))
      return
    }
    setSelectedItemIds([])
  }

  const handleBulkDelete = async () => {
    if (!selectedItemIds.length) return
    const response = await deleteItems(selectedItemIds)
    const deletedCount = response.ids?.length || 0
    const failedCount = Math.max(0, selectedItemIds.length - deletedCount)
    setBulkStatus({
      type: failedCount ? 'warning' : 'success',
      message: `Bulk ștergere: ${deletedCount} reușite, ${failedCount} eșuate.${response.message ? ` ${response.message}` : ''}`
    })
    setSelectedItemIds([])
  }

  const handleOpenBulkDeleteDialog = () => {
    if (!selectedItemIds.length) return
    setBulkDeleteDialogOpen(true)
  }

  const handleCloseBulkDeleteDialog = () => {
    setBulkDeleteDialogOpen(false)
  }

  const handleConfirmBulkDelete = async () => {
    await handleBulkDelete()
    handleCloseBulkDeleteDialog()
  }

  const handleBulkFlag = async ({ field, value, label }) => {
    if (!selectedItemIds.length) return

    const updates = products
      .filter(product => selectedItemIds.includes(product._id))
      .map(product => {
        if (Boolean(product[field]) === value) {
          return null
        }
        return { _id: product._id, [field]: value }
      })
      .filter(Boolean)

    if (!updates.length) return

    const response = await updateItems(updates)
    const successIds = new Set((response.data || []).map(item => item._id))
    const errorIds = new Set((response.errors || []).map(item => item._id))
    const failedCount = errorIds.size || Math.max(0, updates.length - successIds.size)
    setBulkStatus({
      type: failedCount ? 'warning' : 'success',
      message: `${label}: ${successIds.size} reușite, ${failedCount} eșuate.`
    })
    setSelectedItemIds([])
  }

  const normalizeCollections = (collectionIds = []) => [...collectionIds].sort().join(',')

  const getCollectionsWithoutDiscount = (collectionIds = []) => (
    collectionIds.filter(collectionId => !discountCollectionIds.has(collectionId))
  )

  const handleBulkDiscount = async ({ discountId = '', label }) => {
    if (!selectedItemIds.length) return

    const updates = products
      .filter(product => selectedItemIds.includes(product._id))
      .map(product => {
        const currentCollections = Array.isArray(product.collections) ? product.collections : []
        const collectionBase = getCollectionsWithoutDiscount(currentCollections)
        const nextCollections = discountId ? [...collectionBase, discountId] : collectionBase

        if (normalizeCollections(currentCollections) === normalizeCollections(nextCollections)) {
          return null
        }

        return { _id: product._id, collections: nextCollections }
      })
      .filter(Boolean)

    if (!updates.length) return

    const response = await updateItems(updates)
    const successIds = new Set((response.data || []).map(item => item._id))
    const errorIds = new Set((response.errors || []).map(item => item._id))
    const failedCount = errorIds.size || Math.max(0, updates.length - successIds.size)
    setBulkStatus({
      type: failedCount ? 'warning' : 'success',
      message: `${label}: ${successIds.size} reușite, ${failedCount} eșuate.`
    })
    setSelectedItemIds([])
  }

  const handlePriceChange = (event, setFieldValue, values) => {
    const price = event.target.value
    setFieldValue('price', price)
    const discountId = values.discount
    const discount = discounts.find(d => d._id === discountId)
    if (discount) {
      const newPrice = price - (price * (discount.value / 100))
      setDiscountedPrice(newPrice)
    } else {
      setDiscountedPrice(price) // No discount selected
    }
  }

  useEffect(() => {
    setSelectedItemIds(prev => prev.filter(id => products.some(product => product._id === id)))
  }, [products])

  const handleDiscountChange = (event, setFieldValue, values) => {
    const discountId = event.target.value || -1
    setFieldValue('discount', discountId)
    const discount = discounts.find(d => d._id === discountId)
    if (discount) {
      const newPrice = values.price - (values.price * (discount.value / 100))
      setDiscountedPrice(newPrice)
    } else {
      setDiscountedPrice(values.price) // No discount selected
    }
  }

  const handleEdit = (product) => {
    const discountId = product.collections.find(id => discounts.some(discount => discount._id === id))
    const discount = discounts.find(d => d._id === discountId)

    if (discount) {
      const newPrice = product.price - (product.price * (discount.value / 100))
      setDiscountedPrice(newPrice)
    } else {
      setDiscountedPrice(product.price)
    }
    const brandId = product.collections.find(id => brands.some(brand => brand._id === id))
    const categoryIds = categories
      .filter(category => product.collections.includes(category._id))
      .map(category => category._id)
    setInitialValues({
      ...product,
      bestprice: Boolean(product.bestprice),
      discount: discountId || -1,
      brand: brandId || brands[0]._id,
      categories: categoryIds || [categories[0]._id],
      description: product.description.join('\n')
    })
    setOpen(true)
  }

  const buildResolvedFilters = (sourceFilters) => {
    const getBrandIdByName = (name) => {
      const brand = brands.find(b => b.name === name)
      return brand ? brand._id : name
    }

    const getCategoryIdByName = (name) => {
      const category = categories.find(c => c.name === name)
      return category ? category._id : name
    }

    return sourceFilters.map(f => {
      let value = f.value
      if (f.key === 'Brand') {
        value = getBrandIdByName(f.value)
      } else if (f.key === 'Category') {
        value = getCategoryIdByName(f.value)
      }
      return { ...f, value }
    })
  }

  const buildSearchQuery = (sourceFilters) => {
    return buildResolvedFilters(sourceFilters)
      .map(filter => `(${filter.key}${filter.operator}${filter.value})`)
      .join('-')
  }

  const resolveSearchValueForDisplay = useCallback((key, value) => {
    if (key === 'Brand') {
      const brand = brands.find(b => b._id === value)
      return brand ? brand.name : value
    }

    if (key === 'Category') {
      const category = categories.find(c => c._id === value)
      return category ? category.name : value
    }

    return value
  }, [brands, categories])

  const parseSearchQuery = useCallback((searchQuery) => {
    if (!searchQuery) {
      return []
    }

    const segments = searchQuery.match(/\([^()]*\)/g) || []
    const operators = ['!=', '<=', '>=', '<', '>', '=']

    return segments
      .map(segment => {
        const expression = segment.slice(1, -1)
        const operator = operators.find(currentOperator => expression.includes(currentOperator))
        if (!operator) {
          return null
        }

        const [key, ...valueParts] = expression.split(operator)
        const value = valueParts.join(operator)
        if (!key || value === '') {
          return null
        }

        return {
          key,
          operator,
          value: resolveSearchValueForDisplay(key, value)
        }
      })
      .filter(Boolean)
  }, [resolveSearchValueForDisplay])

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries())
    const requestedPage = parseInt(params.page || '1', 10)
    setPage(Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1)
    setSelectedItemIds([])
    fetchItemsByEntityType(null, null, params)
  }, [fetchItemsByEntityType, searchParams])

  useEffect(() => {
    setFilters(parseSearchQuery(searchParams.get('search') || ''))
  }, [parseSearchQuery, searchParams])

  const getPendingFilter = () => {
    if (!selectedKey || !selectedOperator || !selectedValue) {
      return null
    }

    return {
      key: selectedKey,
      operator: selectedOperator,
      value: selectedValue.trim ? selectedValue.trim() : selectedValue
    }
  }

  const appendPendingFilterIfComplete = (sourceFilters) => {
    const pendingFilter = getPendingFilter()
    if (!pendingFilter) {
      return { nextFilters: sourceFilters, appended: false }
    }

    const alreadyExists = sourceFilters.some(filter => (
      filter.key === pendingFilter.key &&
      filter.operator === pendingFilter.operator &&
      String(filter.value) === String(pendingFilter.value)
    ))

    if (alreadyExists) {
      return { nextFilters: sourceFilters, appended: false }
    }

    return {
      nextFilters: [...sourceFilters, pendingFilter],
      appended: true
    }
  }

  const clearPendingFilter = () => {
    setSelectedKey('')
    setSelectedOperator('')
    setSelectedValue('')
  }

  const handleSearch = async () => {
    const { nextFilters, appended } = appendPendingFilterIfComplete(filters)
    if (!nextFilters.length) {
      return
    }

    const searchQuery = buildSearchQuery(nextFilters)

    const updatedFilters = { ...Object.fromEntries(searchParams.entries()), search: searchQuery, page: 1 }
    if (appended) {
      setFilters(nextFilters)
      clearPendingFilter()
    }
    setBulkStatus({ type: '', message: '' })
    setSearchParams(updatedFilters)
    setPage(1)
    setSelectedItemIds([])
  }

  const handleExport = async () => {
    setExportLoading(true)
    const exportQuery = buildResolvedFilters(filters)

    const presignedUrl = await exportItems(exportQuery)
    await downloadFromS3(presignedUrl)
    // await new Promise(resolve => setTimeout(resolve, 2000)) // Simulating a 2 seconds delay

    // console.log('LOADING')
    setExportLoading(false)
  }

  const handleAddFilter = () => {
    const pendingFilter = getPendingFilter()
    if (pendingFilter) {
      setFilters(prevFilters => {
        const alreadyExists = prevFilters.some(filter => (
          filter.key === pendingFilter.key &&
          filter.operator === pendingFilter.operator &&
          String(filter.value) === String(pendingFilter.value)
        ))
        return alreadyExists ? prevFilters : [...prevFilters, pendingFilter]
      })
      clearPendingFilter()
    }
  }

  const handleRemoveFilter = (index) => {
    setFilters(prevFilters => prevFilters.filter((_, i) => i !== index))
  }

  const handleResetFilters = async () => {
    clearPendingFilter()
    setFilters([])
    setBulkStatus({ type: '', message: '' })
    setSelectedItemIds([])
    const updatedParams = { ...Object.fromEntries(searchParams.entries()), page: 1 }
    delete updatedParams.search
    setSearchParams(updatedParams)
    setPage(1)
  }

  const handleOpenDeleteDialog = (item) => {
    setItemToDelete(item)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setItemToDelete(null)
  }

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      handleDelete(itemToDelete)
    }
    handleCloseDeleteDialog()
  }

  const handleSubmit = async (values) => {
    if (fileForUpload) {
      try {
        fileForUpload.map(file => uploadToS3(file).then(console.log('done?')))
      } catch (error) {
        console.error('File upload failed:', error)
      }
    }
    setOpen(false)
    const collectionArray = [values.brand, ...values.categories, ...(values.discount !== -1 ? [values.discount] : [])]
    const payload = {
      _id: values._id,
      name: values.name,
      description: [...values.description.split('\n')],
      price: parseInt(values.price, 10),
      stock: values.stock,
      images: values.images,
      cover: values.images[0],
      bestprice: values.bestprice,
      clearence: values.clearence,
      package: values.package,
      ean: values.ean,
      visible: values.visible,
      collections: collectionArray
    }
    if (values._id === -1) {
      await addItems(payload)
    } else {
      await updateItems(payload)
    }
  }

  const calculateDiscountedPrice = (price, discountRate) => {
    return price - (price * (discountRate / 100))
  }

  const renderPrice = (product) => {
    const discountId = product.collections.find(id => discounts.some(discount => discount._id === id))
    const discount = discounts.find(d => d._id === discountId)
    if (discount) {
      const discountedPrice = calculateDiscountedPrice(product.price, discount.value)
      return (
        <Box>
          <Typography variant='body2' sx={{ fontSize: '15px', fontWeight: 700, color: Colors.fontColor.dark }}>
            <NumericFormat value={discountedPrice} displayType={'text'} thousandSeparator={true} decimalScale={1} suffix={' RON'} />
          </Typography>
          <Typography variant='caption' sx={{ color: Colors.fontColor.medium }}>
            Discount {discount.value}%
          </Typography>
        </Box>
      )
    } else {
      return (
        <Typography variant='body2' sx={{ fontSize: '15px', fontWeight: 700, color: Colors.fontColor.dark }}>
          <NumericFormat value={product.price} displayType={'text'} thousandSeparator={true} suffix={' RON'} />
        </Typography>
      )
    }
  }

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...'
    }
    return text
  }

  const renderDescription = (description) => {
    // Use an empty array as the default value if description is undefined
    const safeDescription = description ?? []
    return truncateText(safeDescription.join('\n'), 50) // You can adjust the length as needed
  }

  const renderStatusChip = ({ active, label, activeBackground, activeBorder, activeColor = '#ffffff' }) => (
    <Chip
      label={active ? label : 'Nu'}
      size='small'
      variant={active ? 'filled' : 'outlined'}
      sx={active
        ? {
            minWidth: 78,
            backgroundColor: activeBackground,
            border: `1px solid ${activeBorder || activeBackground}`,
            color: activeColor,
            fontWeight: 700
          }
        : {
            minWidth: 78,
            color: Colors.fontColor.medium,
            borderColor: Colors.borderColor.main,
            backgroundColor: '#ffffff'
          }}
    />
  )

  const ValueOptions = {
    Name: [],
    Brand: brands.map(brand => { return { name: brand.name, value: brand.name } }),
    Category: categories.map(cat => { return { name: cat.name, value: cat.name } }),
    Visibility: [{ name: 'Da', value: 'True' }, { name: 'Nu', value: 'False' }],
    Package: [{ name: 'Da', value: 'True' }, { name: 'Nu', value: 'False' }],
    BestPrice: [{ name: 'Da', value: 'True' }, { name: 'Nu', value: 'False' }],
    HasDiscount: [{ name: 'Da', value: 'True' }, { name: 'Nu', value: 'False' }],
    Clearence: [{ name: 'Da', value: 'True' }, { name: 'Nu', value: 'False' }],
    Stock: [],
    Price: []
  }

  const filteredKeyOptions = keyOptions.filter(key => !filters.some(filter => filter.key === key.value && filter.key !== 'Category'))
  const hasPendingFilter = Boolean(getPendingFilter())
  const hasAnyFilters = filters.length > 0
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, flexWrap: 'wrap', marginBottom: '20px' }}>
        <Box>
          <Typography variant='h4' sx={{ fontSize: { xs: '28px', md: '36px' }, fontWeight: 700, marginBottom: '8px' }}>
            Produse
          </Typography>
          <Typography variant='body2' sx={{ color: Colors.fontColor.medium, maxWidth: '760px' }}>
            Caută rapid în catalog, aplică filtre compuse și gestionează marcajele BestPrice sau Outlet fără să schimbi regula de discount a produsului.
          </Typography>
        </Box>
        <Button
          startIcon={<Add />}
          variant='outlined'
          onClick={handleAddProduct}
          sx={{ color: Colors.fontColor.main, borderColor: Colors.borderColor.main, whiteSpace: 'nowrap' }}
        >
          Adaugă produs
        </Button>
      </Box>

      <Paper
        variant='outlined'
        sx={{
          marginBottom: '20px',
          padding: { xs: 2, md: 2.5 },
          borderColor: Colors.borderColor.main,
          borderRadius: '18px',
          boxShadow: '0 12px 40px rgba(29, 29, 27, 0.04)'
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', lg: 'center' }, gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, marginBottom: '4px' }}>
                Caută și filtrează
              </Typography>
              <Typography variant='body2' sx={{ color: Colors.fontColor.medium }}>
                Model compact: adaugi reguli, vezi imediat filtrele active și aplici lista atunci când ești gata.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              <Button
                variant='contained'
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                disabled={!hasAnyFilters && !hasPendingFilter}
                sx={{
                  minWidth: 160,
                  backgroundColor: Colors.fontColor.main,
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: Colors.fontColor.dark
                  }
                }}
              >
                Aplică filtrele
              </Button>
              <Button
                variant='outlined'
                onClick={handleResetFilters}
                disabled={!hasAnyFilters && !hasPendingFilter}
                sx={{
                  minWidth: 140,
                  color: Colors.fontColor.main,
                  borderColor: Colors.borderColor.main,
                  '&:hover': {
                    borderColor: Colors.fontColor.main,
                    backgroundColor: 'rgba(49, 49, 49, 0.04)'
                  }
                }}
              >
                Resetează
              </Button>
              <Button
                startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
                variant='contained'
                disabled={!hasAnyFilters || exportLoading}
                onClick={handleExport}
                sx={{
                  minWidth: 150,
                  backgroundColor: Colors.fontColor.main,
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: Colors.fontColor.dark
                  }
                }}
              >
                {exportLoading ? 'Pregătesc CSV...' : 'Export CSV'}
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '180px 160px minmax(0, 1fr) auto' },
              gap: 1.5,
              alignItems: 'center'
            }}
          >
            <FormControl size='small'>
              <InputLabel>Filtru</InputLabel>
              <Select
                value={selectedKey}
                label='Filtru'
                onChange={(e) => {
                  setSelectedKey(e.target.value)
                  setSelectedOperator('')
                  setSelectedValue('')
                }}
              >
            {filteredKeyOptions.map(option => (
              <MenuItem key={option.name} value={option.value}>{option.name}</MenuItem>
            ))}
              </Select>
            </FormControl>
            <FormControl size='small' disabled={!selectedKey}>
              <InputLabel>Operator</InputLabel>
              <Select value={selectedOperator} label='Operator' onChange={(e) => setSelectedOperator(e.target.value)}>
                {selectedKey && operatorOptions[selectedKey].map(option => (
                  <MenuItem key={option.name} value={option.value}>{option.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedKey && ValueOptions[selectedKey].length > 0
              ? (
                <FormControl size='small' sx={{ width: '100%' }}>
                  <InputLabel>Valoare</InputLabel>
                  <Select value={selectedValue} label='Valoare' onChange={(e) => setSelectedValue(e.target.value)}>
                    {ValueOptions[selectedKey].map(option => (
                      <MenuItem key={option.name} value={option.value}>{option.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                )
              : (
                <TextField
                  variant='outlined'
                  size='small'
                  label='Valoare'
                  value={selectedValue}
                  onChange={(e) => setSelectedValue(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (hasAnyFilters || hasPendingFilter)) {
                      handleSearch()
                    }
                  }}
                  sx={{ width: '100%' }}
                />
                )}
            <Button
              variant='outlined'
              startIcon={<Add />}
              onClick={handleAddFilter}
              disabled={!hasPendingFilter}
              sx={{
                minHeight: 40,
                color: Colors.fontColor.main,
                borderColor: Colors.borderColor.main,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: Colors.fontColor.main,
                  backgroundColor: 'rgba(49, 49, 49, 0.04)'
                }
              }}
            >
              Adaugă filtru
            </Button>
          </Box>

          {hasAnyFilters
            ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {filters.map((filter, index) => (
                  <Chip
                    key={`${filter.key}-${filter.operator}-${filter.value}-${index}`}
                    label={`${filterKeyLabels[filter.key] || filter.key} ${filter.operator} ${boolFilterValues.has(filter.key) ? (String(filter.value).toLowerCase() === 'true' ? 'Da' : 'Nu') : filter.value}`}
                    onDelete={() => handleRemoveFilter(index)}
                    variant='outlined'
                    sx={{
                      backgroundColor: '#fafafa',
                      borderColor: Colors.borderColor.main,
                      color: Colors.fontColor.main
                    }}
                  />
                ))}
              </Box>
              )
            : (
              <Typography variant='body2' sx={{ color: Colors.fontColor.light }}>
                Nu ai filtre active. Construiește unul mai sus sau caută direct după ce ai completat regula.
              </Typography>
              )}
        </Stack>
      </Paper>

      <Paper
        variant='outlined'
        sx={{
          marginBottom: '20px',
          padding: { xs: 2, md: 2.25 },
          borderColor: selectedItemIds.length ? 'rgba(226, 6, 19, 0.22)' : Colors.borderColor.main,
          borderRadius: '18px'
        }}
      >
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', xl: 'center' }, flexDirection: { xs: 'column', xl: 'row' }, gap: 2 }}>
            <Stack spacing={0.75}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${selectedItemIds.length} selectate`}
                  size='small'
                  variant={selectedItemIds.length ? 'filled' : 'outlined'}
                  sx={selectedItemIds.length
                    ? {
                        backgroundColor: Colors.accent.main,
                        color: '#ffffff',
                        fontWeight: 700
                      }
                    : {
                        color: Colors.fontColor.medium,
                        borderColor: Colors.borderColor.main
                      }}
                />
              <Typography variant='body2' sx={{ color: Colors.fontColor.medium }}>
                Acțiunile bulk se aplică doar produselor bifate din lista încărcată acum.
              </Typography>
            </Box>
            <Typography variant='body2' sx={{ color: Colors.fontColor.main }}>
                BestPrice și Outlet sunt marcaje separate; discountul rămâne configurat per produs și poate fi aplicat separat în bulk.
              </Typography>
              {bulkStatus.message && (
                <Typography
                  variant='body2'
                  sx={{
                    color: bulkStatus.type === 'error' ? Colors.accent.main : (bulkStatus.type === 'warning' ? '#e65100' : '#2e7d32')
                  }}
                >
                  {bulkStatus.message}
                </Typography>
              )}
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap='wrap'>
              <FormControl
                size='small'
                sx={{
                  minWidth: 240,
                  backgroundColor: '#ffffff'
                }}
              >
                <InputLabel id='bulk-discount-select-label'>Discount</InputLabel>
                <Select
                  labelId='bulk-discount-select-label'
                  value={selectedBulkDiscountId}
                  label='Discount'
                  onChange={(event) => setSelectedBulkDiscountId(event.target.value)}
                >
                  <MenuItem value=''>
                    Fără discount
                  </MenuItem>
                  {discounts.map((discount) => (
                    <MenuItem key={discount._id} value={discount._id}>
                      {discount.name} ({discount.discount_type === 'percent' ? `${discount.value}%` : `${discount.value} RON`})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant='contained'
                size='small'
                disabled={!selectedItemIds.length || !selectedBulkDiscountId}
                onClick={() => handleBulkDiscount({ discountId: selectedBulkDiscountId, label: 'Aplicare discount' })}
                sx={{
                  minWidth: 180,
                  backgroundColor: Colors.fontColor.main,
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: Colors.fontColor.dark
                  }
                }}
              >
                Aplică discount
              </Button>
              <Button
                variant='outlined'
                size='small'
                disabled={!selectedItemIds.length}
                onClick={() => handleBulkDiscount({ discountId: '', label: 'Eliminare discount' })}
                sx={{
                  minWidth: 180,
                  color: Colors.fontColor.main,
                  borderColor: Colors.borderColor.main,
                  '&:hover': {
                    borderColor: Colors.fontColor.main,
                    backgroundColor: 'rgba(49, 49, 49, 0.04)'
                  }
                }}
              >
                Scoate discountul
              </Button>
              <Button
                variant='contained'
                size='small'
                disabled={!selectedItemIds.length}
                onClick={() => handleBulkFlag({ field: 'bestprice', value: true, label: 'Marcare BestPrice' })}
                sx={{
                  minWidth: 180,
                  backgroundColor: Colors.accent.main,
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: Colors.accent.light
                  }
                }}
              >
                Adaugă în BestPrice
              </Button>
              <Button
                variant='outlined'
                size='small'
                disabled={!selectedItemIds.length}
                onClick={() => handleBulkFlag({ field: 'bestprice', value: false, label: 'Eliminare marcaj BestPrice' })}
                sx={{
                  minWidth: 180,
                  color: Colors.fontColor.main,
                  borderColor: Colors.borderColor.main,
                  '&:hover': {
                    borderColor: Colors.fontColor.main,
                    backgroundColor: 'rgba(49, 49, 49, 0.04)'
                  }
                }}
              >
                Scoate din BestPrice
              </Button>
              <Button
                variant='contained'
                size='small'
                disabled={!selectedItemIds.length}
                onClick={() => handleBulkFlag({ field: 'clearence', value: true, label: 'Marcare Outlet' })}
                sx={{
                  minWidth: 180,
                  backgroundColor: '#f57c00',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#ef6c00'
                  }
                }}
              >
                Marchează Outlet
              </Button>
              <Button
                variant='outlined'
                size='small'
                disabled={!selectedItemIds.length}
                onClick={() => handleBulkFlag({ field: 'clearence', value: false, label: 'Eliminare marcaj Outlet' })}
                sx={{
                  minWidth: 180,
                  color: '#8d4b00',
                  borderColor: '#f57c00',
                  '&:hover': {
                    borderColor: '#ef6c00',
                    backgroundColor: 'rgba(245, 124, 0, 0.04)'
                  }
                }}
              >
                Scoate din Outlet
              </Button>
              <Button
                variant='outlined'
                size='small'
                color='error'
                disabled={!selectedItemIds.length}
                onClick={handleOpenBulkDeleteDialog}
                sx={{ minWidth: 210 }}
              >
                Șterge produsele selectate
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <TableContainer>
        <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                color='fontColor'
                checked={products.length > 0 && selectedItemIds.length === products.length}
                indeterminate={selectedItemIds.length > 0 && selectedItemIds.length < products.length}
                onChange={handleToggleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Nume</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Descriere</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Preț final</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>BestPrice</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Outlet</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Pachet</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Vizibil</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Acțiuni</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
            {products.map(product =>
            <TableRow key={product._id} hover>
              <TableCell padding='checkbox'>
                <Checkbox
                  color='fontColor'
                  checked={selectedItemIds.includes(product._id)}
                  onChange={() => handleToggleSelected(product._id)}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 500, maxWidth: 320 }}>{product.name}</TableCell>
              <TableCell sx={{ color: Colors.fontColor.medium, maxWidth: 420 }}>{renderDescription(product.description)}</TableCell>
              <TableCell>{renderPrice(product)}</TableCell>
              <TableCell>{renderStatusChip({ active: Boolean(product.bestprice), label: 'Da', activeBackground: Colors.accent.main, activeBorder: Colors.accent.main })}</TableCell>
              <TableCell>{renderStatusChip({ active: Boolean(product.clearence), label: 'Da', activeBackground: '#f57c00', activeBorder: '#ef6c00' })}</TableCell>
              <TableCell>{renderStatusChip({ active: Boolean(product.package), label: 'Da', activeBackground: '#212252', activeBorder: '#212252' })}</TableCell>
              <TableCell>{renderStatusChip({ active: Boolean(product.visible), label: 'Da', activeBackground: '#2e7d32', activeBorder: '#2e7d32' })}</TableCell>
              <TableCell>
                <Tooltip title='Editează produsul'>
                  <IconButton onClick={() => handleEdit(product)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Șterge produsul'>
                  <IconButton onClick={() => handleOpenDeleteDialog(product)}>
                    <DeleteForever sx={{ color: Colors.accent.main }}/>
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
            )}
        </TableBody>
        </Table>
      </TableContainer>
      <Box padding='25px 0'>
          <Pagination count={totalPages} page={page} onChange={paginationHandleChange} />
      </Box>
      <Dialog
      open={open}
      fullWidth
      maxWidth='lg'
      >
        <DialogTitle>
              {'Adaugă/modifică produsul'}
        </DialogTitle>
        <Formik initialValues={initialValues} validationSchema={validationData}
        onSubmit={handleSubmit}>
          {({ values, dirty, isValid, getFieldProps, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name='name'
                    label='Nume produs'
                    size="small"
                    fullWidth
                    required
                  />
                  <ErrorMessage name='name'>
                    {(message) => (
                      <Typography color={'red'}>{message}</Typography>
                    )}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name='ean'
                    label='EAN code'
                    size="small"
                    fullWidth
                    required
                  />
                  <ErrorMessage name='ean'>
                    {(message) => (
                      <Typography color={'red'}>{message}</Typography>
                    )}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name='description'
                    label='Descriere produs'
                    multiline
                    rows={10}
                    fullWidth
                    required
                  />
                  <ErrorMessage name='description'>
                    {(message) => (
                      <Typography color={'red'}>{message}</Typography>
                    )}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name='price'
                    label='Preț'
                    size="small"
                    onChange={(event) => handlePriceChange(event, setFieldValue, values)}
                    fullWidth
                    required
                  />
                  <ErrorMessage name='price'>
                    {(message) => (
                      <Typography color={'red'}>{message}</Typography>
                    )}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                <Field name="categories">
                  {({ field, form }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="categories-select-label">Categories</InputLabel>
                      <Select
                        labelId="categories-select-label"
                        id="categories-select"
                        multiple
                        {...field}
                        value={field.value}
                        onChange={event => {
                          form.setFieldValue('categories', event.target.value)
                        }}
                        input={<OutlinedInput label="Categorie" />}
                        renderValue={selected => selected
                          .map(id => categories.find(category => category._id === id)?.name)
                          .join(', ')
                        }
                        MenuProps={MenuProps}
                      >
                      {categories.map(category => (
                          <MenuItem key={category._id} value={category._id}>
                            <Checkbox checked={field.value.includes(category._id)} color='fontColor' />
                            <ListItemText primary={category.name} />
                          </MenuItem>
                      ))}
                      </Select>
                    </FormControl>
                  )}
                </Field>
                  <ErrorMessage name='category'>
                    {(message) => (
                      <Typography color={'red'}>{message}</Typography>
                    )}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                  <Field name="brand">
                  {({ field, form }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="brand-select-label">Brand</InputLabel>
                      <Select
                        labelId="brand-select-label"
                        id="brand-select"
                        {...field}
                        label="Brand"
                        onChange={(event) => {
                          form.setFieldValue('brand', event.target.value)
                        }}
                        renderValue={(selectedId) => {
                          return brands.find(brand => brand._id === selectedId)?.name || ''
                        }}
                      >
                        {brands.map((brand) => (
                          <MenuItem key={brand._id} value={brand._id} sx={{ textTransform: 'capitalize' }}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Field>
                <ErrorMessage name='brand'>
                  {(message) => (
                    <Typography color={'red'}>{message}</Typography>
                  )}
                </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                  <Field name="discount">
                  {({ field, form }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="discount-select-label">Discount</InputLabel>
                      <Select
                        labelId="discount-select-label"
                        id="discount-select"
                        {...field}
                        label="Discount"
                        onChange={event => handleDiscountChange(event, setFieldValue, values)}
                        renderValue={(selectedId) => {
                          const discountData = discounts.find(discount => discount._id === selectedId)
                          const textToRender = discountData?.name ? `${discountData.value}% Preț cu discount: ${discountedPrice.toFixed(1)}` : 'None'
                          return textToRender
                        }}
                      >
                        <MenuItem key={-1} value={-1}>
                          {'None'}
                        </MenuItem>
                        {discounts.map((discount) => (
                          <MenuItem key={discount._id} value={discount._id}>
                            {discount.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Field>
                <ErrorMessage name='discount'>
                  {(message) => (
                    <Typography color={'red'}>{message}</Typography>
                  )}
                </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    component={Dropzone}
                    allowMultiple={true}
                    name='images'
                    onFilesChange={(files) => {
                      const fileNames = files.map(file => file.hashedName ? file.hashedName : file.preview)
                      setFieldValue('images', fileNames)
                      setFileForUpload(files.length ? files : null)
                    }}
                    className= 'brands-dragndrop'
                    existingLogoUrl={initialValues.images}
                  />
                  <ErrorMessage name='images'>
                    {(message) => (
                      <Typography color={'red'}>{message}</Typography>
                    )}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={2}>
                  <Field name='bestprice'>
                    {({
                      field,
                      form: { setFieldValue }
                    }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color='fontColor'
                              {...field}
                              checked={field.value}
                              onChange={(event) => setFieldValue('bestprice', event.target.checked)}
                            />
                          }
                          label="BestPrice"
                        />
                      </FormGroup>
                    )}
                  </Field>
                  <ErrorMessage name='bestprice'>
                    {(message) => <Typography color={'red'}>{message}</Typography>}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={2}>
                  <Field name='clearence'>
                    {({
                      field, // { name, value, onChange, onBlur }
                      form: { setFieldValue } // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                    }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color='fontColor'
                              {...field}
                              checked={field.value} // the value will be a boolean indicating checked state
                              onChange={(event) => setFieldValue('clearence', event.target.checked)} // update Formik state
                            />
                          }
                          label="Outlet"
                        />
                      </FormGroup>
                    )}
                  </Field>
                  <ErrorMessage name='clearence'>
                    {(message) => <Typography color={'red'}>{message}</Typography>}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={2}>
                  <Field name='package'>
                    {({
                      field, // { name, value, onChange, onBlur }
                      form: { setFieldValue } // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                    }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color='fontColor'
                              {...field}
                              checked={field.value} // the value will be a boolean indicating checked state
                              onChange={(event) => setFieldValue('package', event.target.checked)} // update Formik state
                            />
                          }
                          label="Pachet"
                        />
                      </FormGroup>
                    )}
                  </Field>
                  <ErrorMessage name='package'>
                    {(message) => <Typography color={'red'}>{message}</Typography>}
                  </ErrorMessage>
                </Grid>
                <Grid item xs={2}>
                  <Field name='visible'>
                    {({
                      field, // { name, value, onChange, onBlur }
                      form: { setFieldValue } // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                    }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color='fontColor'
                              {...field}
                              checked={field.value} // the value will be a boolean indicating checked state
                              onChange={(event) => setFieldValue('visible', event.target.checked)} // update Formik state
                            />
                          }
                          label="Visible"
                        />
                      </FormGroup>
                    )}
                  </Field>
                  <ErrorMessage name='visible'>
                    {(message) => <Typography color={'red'}>{message}</Typography>}
                  </ErrorMessage>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {
                getFieldProps('_id').value !== -1
                  ? (
                <Button
               disabled={!dirty || !isValid}
               type='submit' variant='contained' color='primary'>Save Edit</Button>
                    )
                  : (
                <Button
               disabled={!dirty || !isValid}
               type='submit' variant='contained' color='primary'>Save</Button>
                    )
               }
              <Button autoFocus variant='contained' onClick={() => setOpen(false)}>Cancel</Button>
            </DialogActions>
          </Form>

          )}
        </Formik>
      </Dialog>
      <DeleteConfirmationDialog
      open={deleteDialogOpen}
      onClose={handleCloseDeleteDialog}
      onConfirm={handleConfirmDelete}
      object={itemToDelete ? itemToDelete.name : ''}
      />
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={handleCloseBulkDeleteDialog}
        aria-labelledby='bulk-delete-dialog-title'
        aria-describedby='bulk-delete-dialog-description'
      >
        <DialogTitle id='bulk-delete-dialog-title'>Confirmă ștergerea bulk</DialogTitle>
        <DialogContent>
          <DialogContentText id='bulk-delete-dialog-description'>
            {`Vei șterge ${selectedItemIds.length} produse selectate de pe pagina curentă. Această acțiune este definitivă.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDeleteDialog} color='secondary'>
            Anulează
          </Button>
          <Button onClick={handleConfirmBulkDelete} color='error' autoFocus>
            Șterge produsele selectate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Products
